/**
 * Phase 2B — Termination workflow (Owner/HR).
 *
 * Pick employee → reason + effective date → Preview (live calc of
 * severance per LPA §118 + accrued leave + notice pay) → Commit
 * (snapshots termination_records, feeds payroll adjustment, marks
 * employee resigned/terminated).
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  useEmployees,
  usePreviewTermination,
  useCommitTermination,
  type TerminationPreview,
} from '../../hooks';
import { UserMinus, Calculator, CheckCircle2 } from 'lucide-react';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import { ComplianceAlertList, type ComplianceItem } from '../../components/ui/ComplianceAlert';
import { Term } from '../../components/ui/TermTooltip';
import { EmployeePicker } from '../../components/ui/EmployeePicker';
import { Modal } from '../../components/ui/Modal';

const REASONS: Array<{ value: string; label: string; note?: string }> = [
  { value: 'resigned', label: 'ลาออก (ไม่มีชดเชย)' },
  { value: 'terminated', label: 'เลิกจ้าง (มีชดเชยตามอายุงาน)' },
  { value: 'terminated_for_cause', label: 'เลิกจ้างเพราะลูกจ้างมีความผิด (ม.119)', note: 'ทุจริต / จงใจให้เสียหาย / ขาดงาน 3 วันติด / ฝ่าฝืนระเบียบหลังเตือน — ไม่มีชดเชย + ไม่ต้องบอกกล่าว' },
  { value: 'retired', label: 'เกษียณ' },
  { value: 'contract_end', label: 'ครบสัญญา' },
];

function fmt(n: number): string {
  return new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}
function thisMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

/**
 * ระบบรู้กฎหมายแรงงานแทนผู้ใช้ที่ไม่ใช่ HR (Non-HR UX §4) — แปลงเหตุผลออกจากงาน
 * เป็นข้อเตือน/ข้อควรรู้เชิงกฎหมายก่อนกดยืนยัน. เป็นคำแนะนำ ไม่ใช่คำวินิจฉัย
 * (suggest-only — ดู HUMANAI_DIRECTION §9): ทุกข้ออ้างมาตราจริง + ปิดท้ายให้ปรึกษาทนาย.
 */
function buildTerminationAlerts(reason: string, noticeShortfall: number): ComplianceItem[] {
  const items: ComplianceItem[] = [];
  if (reason === 'terminated_for_cause') {
    items.push({
      level: 'block',
      title: 'เลิกจ้างเพราะความผิด — ต้องมีหลักฐานชัดเจน',
      detail: 'ต้องมีหนังสือเตือน/บันทึกความผิดที่เข้าเงื่อนไข (ทุจริต จงใจให้เสียหาย ขาดงาน 3 วันติดต่อกัน หรือฝ่าฝืนระเบียบหลังเตือนเป็นหนังสือ) มิฉะนั้นพนักงานฟ้อง "เลิกจ้างไม่เป็นธรรม" ได้ และอาจต้องจ่ายค่าชดเชย + ค่าเสียหายย้อนหลัง',
      cite: 'พ.ร.บ.คุ้มครองแรงงาน ม.119 · ม.49 (ศาลแรงงาน)',
    });
  }
  if (reason === 'terminated') {
    items.push({
      level: 'info',
      title: 'การเลิกจ้างนี้ต้องจ่ายค่าชดเชยตามอายุงาน',
      detail: 'ระบบคำนวณค่าชดเชยตามอายุงานให้แล้ว — เป็นเงินที่กฎหมายบังคับจ่ายเมื่อเลิกจ้างโดยลูกจ้างไม่ได้ทำผิด',
      cite: 'พ.ร.บ.คุ้มครองแรงงาน ม.118',
    });
    if (noticeShortfall <= 0) {
      items.push({
        level: 'warn',
        title: 'ให้ออกทันทีหรือเปล่า? อาจต้องจ่ายค่าบอกกล่าวล่วงหน้า',
        detail: 'ถ้าไม่ได้บอกล่วงหน้าอย่างน้อย 1 งวดการจ่ายค่าจ้าง ต้องจ่าย "ค่าบอกกล่าว" แทน — กรอกจำนวนวันที่ขาดในช่อง "วันบอกกล่าวที่ขาด" ให้ถูกต้อง',
        cite: 'พ.ร.บ.คุ้มครองแรงงาน ม.17',
      });
    }
  }
  if (reason === 'resigned') {
    items.push({
      level: 'info',
      title: 'ลาออกเอง — ไม่ต้องจ่ายค่าชดเชย',
      detail: 'แต่ยังต้องจ่ายค่าจ้างค้างจ่ายและวันลาพักร้อนสะสมที่เหลือ',
    });
  }
  return items;
}

/** Content-only version used by the People hub. */
export function OwnerTerminationContent() {
  const { data: employees } = useEmployees() as {
    data: Array<{ emp_code: string; first_name: string; last_name: string; nickname?: string | null; status: string; department?: string | null; position?: string | null }> | undefined;
  };
  const preview = usePreviewTermination();
  const commit = useCommitTermination();
  const toast = useToast();
  const consequence = useConsequence();

  const [empCode, setEmpCode] = useState('');
  const [reason, setReason] = useState('terminated');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [noticeShortfall, setNoticeShortfall] = useState(0);
  const [feedsPeriod, setFeedsPeriod] = useState(thisMonth());
  const [result, setResult] = useState<TerminationPreview | null>(null);
  const [successName, setSuccessName] = useState<string | null>(null);

  const activeEmployees = (employees ?? []).filter((e) => e.status === 'active' || e.status === 'probation');

  async function handlePreview() {
    if (!empCode) { toast.error('เลือกพนักงาน'); return; }
    try {
      const r = await preview.mutateAsync({
        emp_code: empCode,
        effective_date: effectiveDate,
        reason_code: reason,
        notice_shortfall_days: noticeShortfall,
      });
      setResult(r);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  /** ยืนยัน+บันทึกจริง โดยใช้ยอดที่คำนวณแล้ว (r) — ใช้ร่วมทั้งปุ่ม "ยืนยัน" และ "ออกเลย" */
  async function doCommit(r: TerminationPreview) {
    const ok = await consequence({
      title: `ยืนยันให้ ${r.emp_name} ออกจากงาน`,
      intro: 'เมื่อยืนยัน ระบบจะบันทึกการออกจากงานและส่งยอดจ่ายเข้าเงินเดือน',
      lines: [
        { label: `ค่าชดเชย (${r.severance_days} วัน)`, value: `฿${fmt(r.severance_amount)}`, tone: 'money' },
        { label: `วันลาสะสม (${r.accrued_leave_days} วัน)`, value: `฿${fmt(r.accrued_leave_amount)}`, tone: 'money' },
        { label: 'ค่าบอกกล่าวล่วงหน้า', value: `฿${fmt(r.notice_pay)}`, tone: 'money' },
        { label: 'รวมจ่ายทั้งสิ้น', value: `฿${fmt(r.total_payout)}`, tone: 'money', emphasis: true },
      ],
      effects: [
        `ส่งยอดจ่ายเข้าเงินเดือนงวด ${feedsPeriod}`,
        'เปลี่ยนสถานะพนักงานเป็นออกจากงาน (ย้อนกลับยาก)',
      ],
      alerts: [
        ...buildTerminationAlerts(reason, noticeShortfall),
        {
          level: 'info',
          title: 'ยอดนี้เป็นการคำนวณเบื้องต้น',
          detail: 'กรณีซับซ้อน (เช่น มีข้อพิพาท) ควรปรึกษาทนายก่อนแจ้งพนักงาน',
        },
      ],
      danger: true,
      confirmText: 'ยืนยันออกจากงาน',
    });
    if (!ok) return;
    try {
      await commit.mutateAsync({
        emp_code: empCode,
        effective_date: effectiveDate,
        reason_code: reason,
        notice_shortfall_days: noticeShortfall,
        feeds_payroll_period: feedsPeriod,
      });
      // สำเร็จ → เด้งป๊อปอัปยืนยัน แล้วเคลียร์ฟอร์มให้พร้อมทำคนต่อไป
      setSuccessName(r.emp_name);
      setEmpCode('');
      setReason('terminated');
      setNoticeShortfall(0);
      setEffectiveDate(new Date().toISOString().slice(0, 10));
      setResult(null);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  /** ปุ่ม "ยืนยันออกจากงาน" ในการ์ดผลคำนวณ */
  function handleCommit() {
    if (!result) return;
    void doCommit(result);
  }

  /** ปุ่ม "ออกเลย" — ดึงยอดให้อัตโนมัติ แล้วเข้าหน้ายืนยันทันที (ไม่ต้องกดคำนวณก่อน) */
  async function handleQuickCommit() {
    if (!empCode) { toast.error('เลือกพนักงาน'); return; }
    try {
      const r = await preview.mutateAsync({
        emp_code: empCode,
        effective_date: effectiveDate,
        reason_code: reason,
        notice_shortfall_days: noticeShortfall,
      });
      setResult(r);
      await doCommit(r);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  return (
    <>
      <p className="text-sm text-gray-600 mb-3">คำนวณค่าชดเชย + วันลาสะสม + ค่าบอกกล่าว</p>
      <div className="card p-4 space-y-3 mb-4">
        <div>
          <label className="label">พนักงาน *</label>
          <EmployeePicker
            employees={activeEmployees}
            value={empCode || null}
            onChange={(c) => { setEmpCode(c ?? ''); setResult(null); }}
            placeholder="— เลือก —"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">เหตุผล</label>
            <select value={reason} onChange={(e) => { setReason(e.target.value); setResult(null); }} className="input">
              {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label">วันสุดท้ายที่ทำงาน</label>
            <input type="date" value={effectiveDate} onChange={(e) => { setEffectiveDate(e.target.value); setResult(null); }} className="input" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">วันบอกกล่าวที่ขาด</label>
            <input type="number" min={0} value={noticeShortfall} onChange={(e) => { setNoticeShortfall(Number(e.target.value)); setResult(null); }} className="input" />
          </div>
          <div>
            <label className="label">Feed เข้า payroll งวด</label>
            <input type="month" value={feedsPeriod} onChange={(e) => setFeedsPeriod(e.target.value)} className="input" />
          </div>
        </div>
        {/* เลือกได้: คำนวณดูก่อน หรือ ออกเลย — ปุ่มชัดเจน 2 ปุ่ม */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
          <button onClick={handlePreview} disabled={preview.isPending || commit.isPending} className="btn-secondary w-full py-3 text-base">
            <Calculator className="w-5 h-5" /> {preview.isPending ? 'กำลังคำนวณ…' : 'คำนวณดูก่อน'}
          </button>
          <button onClick={handleQuickCommit} disabled={preview.isPending || commit.isPending} className="btn-danger w-full py-3 text-base">
            <UserMinus className="w-5 h-5" /> {commit.isPending ? 'กำลังบันทึก…' : 'ออกจากงานเลย'}
          </button>
        </div>
        <p className="text-xs text-gray-400 text-center">กด “คำนวณดูก่อน” เพื่อตรวจยอดก่อน หรือ “ออกจากงานเลย” ระบบจะคำนวณให้แล้วให้ยืนยันทันที</p>
      </div>

      {/* เตือนเชิงกฎหมายตามเหตุผลที่เลือก (Non-HR UX §4) — โผล่ทันทีก่อนกดคำนวณ */}
      <div className="mb-4">
        <ComplianceAlertList items={buildTerminationAlerts(reason, noticeShortfall)} />
      </div>

      {result && (
        <div className="card p-4 mb-4">
          <div className="font-semibold mb-2">{result.emp_name}</div>
          <div className="text-xs text-gray-500 mb-3">
            อายุงาน {result.tenure_years} ปี ({result.tenure_days} วัน) · daily rate {fmt(result.daily_rate)}
          </div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              <tr>
                <td className="py-1.5"><Term name="severance" /> ({result.severance_days} วัน)</td>
                <td className="py-1.5 text-right">{fmt(result.severance_amount)}</td>
              </tr>
              <tr>
                <td className="py-1.5"><Term name="accrued_leave" /> ({result.accrued_leave_days} วัน)</td>
                <td className="py-1.5 text-right">{fmt(result.accrued_leave_amount)}</td>
              </tr>
              <tr>
                <td className="py-1.5"><Term name="notice_pay" /></td>
                <td className="py-1.5 text-right">{fmt(result.notice_pay)}</td>
              </tr>
              <tr className="border-t-2 font-bold">
                <td className="py-2">รวมจ่าย</td>
                <td className="py-2 text-right text-brand-700">{fmt(result.total_payout)}</td>
              </tr>
            </tbody>
          </table>

          <button onClick={handleCommit} disabled={commit.isPending} className="btn-danger w-full mt-3">
            <UserMinus className="w-4 h-4" /> {commit.isPending ? 'กำลังบันทึก…' : 'ยืนยันออกจากงาน'}
          </button>
        </div>
      )}

      {/* ป๊อปอัปยืนยันบันทึกสำเร็จ — ปิดแล้วฟอร์มถูกเคลียร์รอคนต่อไป */}
      <Modal open={!!successName} onClose={() => setSuccessName(null)}>
        <div className="text-center py-2">
          <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <div className="font-semibold text-lg mb-1">บันทึกข้อมูลการลาออกสำเร็จ</div>
          {successName && (
            <div className="text-sm text-gray-500 mb-4">บันทึก {successName} ออกจากงานเรียบร้อยแล้ว</div>
          )}
          <button onClick={() => setSuccessName(null)} className="btn-cta w-full">
            ทำรายการถัดไป
          </button>
        </div>
      </Modal>
    </>
  );
}

/** Standalone page kept for legacy /owner/termination (now redirects via People hub). */
export function OwnerTermination() {
  return (
    <AdminLayout title="พนักงานออกจากงาน">
      <OwnerTerminationContent />
    </AdminLayout>
  );
}
